import { endOfDay, isBefore, isSameDay, startOfDay } from 'date-fns';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { formatDate } from '@dynatrace-sdk/units';

import { useCalendarContext } from './useCalendarContext.js';
import { useFocus } from './useFocus.js';
import { isTimeframeV2 } from '../../forms-core/time/types.js';
import type { ViewItem } from '../CalendarItem.js';
import { _isOutOfBounds } from '../state/calendar-validation.js';
import type { UseCalendarItemProps } from '../types/shared-types.js';
import { getHighlightingProps } from '../utils/helpers.js';

/** Custom hook that retrieves properties from the current day. */
export function useDay({ date, itemRef }: UseCalendarItemProps): ViewItem {
  const { state, dispatch } = useCalendarContext();

  const intl = useIntl();

  const ariaLabel = useMemo(() => {
    const formatMessage = intl.formatMessage({
      defaultMessage: 'Select day',
      id: 'UBJSASimMVkN5w7p',
      description: 'Aria label to add to each day button',
    });
    return `${formatMessage} ${formatDate(date, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    })}`;
  }, [date, intl]);

  const isCurrent = useMemo(
    () => isSameDay(date, state.today),
    [date, state.today],
  );

  const isFocused = useMemo(
    () => isSameDay(date, state.activeDate),
    [date, state.activeDate],
  );

  const {
    highlightType,
    highlightColor,
    isSelected,
    selectionVariant,
    isIncompleteSelection,
  } = useMemo(() => getHighlightingProps(date, state), [date, state]);

  const isDisabled = useMemo(
    () =>
      // A day is disabled if its end is before the min, or if its start is after the max.
      _isOutOfBounds(endOfDay(date), state.min) ||
      _isOutOfBounds(date, undefined, state.max),
    [date, state.min, state.max],
  );

  const { tabIndex } = useFocus({ isFocused, itemRef });

  /** Handle click on a day in the select day view. */
  function onClick() {
    dispatch({ type: 'isCalendarBodyFocusedChange', payload: false });

    if (state.position !== 'to') {
      dispatch({
        type: 'selectionChanged',
        payload: startOfDay(date).toISOString(),
      });
      return;
    }

    const selectedFromValue = isTimeframeV2(state.selectedDate, 'range')
      ? state.selectedDate?.from?.value
      : null;
    const isToBeforeFrom = selectedFromValue
      ? isBefore(date, selectedFromValue)
      : false;
    const dateTime = isToBeforeFrom ? startOfDay(date) : endOfDay(date);

    dispatch({
      type: 'selectionChanged',
      payload: dateTime.toISOString(),
    });
  }

  const rangeInteractionProps =
    // only need to listen to hover if only one date is selected in a range.
    isTimeframeV2(state.selectedDate, state.type) &&
    state.selectedDate.from &&
    !state.selectedDate.to
      ? {
          onMouseEnter: () => {
            dispatch({
              type: 'hoveredItemChanged',
              payload: date,
            });
          },
        }
      : {};

  return {
    ariaLabel,
    tabIndex,
    isCurrent,
    onClick,
    isDisabled,
    rangeInteractionProps,
    isSelected,
    isIncompleteSelection,
    highlightColor,
    highlightType,
    selectionVariant,
  };
}
